import React, { useEffect, useState } from 'react';
import { fmtNumber } from '../shared/format';
import { fileUrl } from '../../api/portal';

/**
 * FileChip — one attached file inside a message bubble or the composer (Epic 7 · S7.1).
 *
 * Two modes (Part A.7):
 *   1. RECEIVED — the file already lives in the `portal-files` bucket (`file.path`).
 *      Fetches a 1-hour signed URL on mount via `fileUrl(path)` and renders the
 *      name, size (`fmtNumber`), and an image thumbnail when the file is an image
 *      (same signed link). Clicking opens the signed URL in a new tab.
 *   2. UPLOADING — the composer is pushing the file (`file.progress` 0–100).
 *      Renders the name + a progress bar; not clickable until complete.
 *
 * Never imports `supabase` directly — `fileUrl` is the single data-access surface
 * (Epic 1). `onFetchUrl` is injectable so tests don't hit the network.
 */
const isImageType = (type) => !!type && String(type).startsWith('image/');

const FileChip = ({ file, onFetchUrl }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);

  const path = file?.path;
  const uploading = file?.uploading === true || (typeof file?.progress === 'number' && file.progress < 100);
  const progress = typeof file?.progress === 'number' ? file.progress : null;
  const image = isImageType(file?.type) || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(file?.name || '');

  useEffect(() => {
    let active = true;
    if (path && !uploading) {
      (async () => {
        try {
          const resolved = onFetchUrl ? await onFetchUrl(path) : await fileUrl(path);
          if (active) setUrl(resolved);
        } catch (e) {
          if (active) setErr(e?.message || 'Couldn’t load file.');
        }
      })();
    }
    return () => { active = false; };
  }, [path, uploading, onFetchUrl]);

  const open = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <span
      data-testid="file-chip"
      data-uploading={uploading ? 'true' : undefined}
      className={`inline-flex items-center gap-2 max-w-[220px] rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-2 py-1 text-xs ${
        uploading ? 'opacity-80' : 'cursor-pointer hover:border-[#32B1FF]'
      }`}
      onClick={uploading ? undefined : open}
      title={file?.name || ''}
    >
      {image && !uploading && (
        url ? (
          <img src={url} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
        ) : (
          <span className="w-8 h-8 rounded bg-gray-200 dark:bg-gray-700 shrink-0" />
        )
      )}
      <span className="min-w-0 flex flex-col">
        <span className="truncate text-gray-900 dark:text-gray-100 font-medium">{file?.name || 'file'}</span>
        <span className="text-gray-500 dark:text-gray-400">
          {uploading
            ? (progress != null ? `${progress}%` : 'Uploading…')
            : (typeof file?.size === 'number' ? fmtNumber(file.size) + ' B' : '')}
        </span>
      </span>
      {err && <span className="text-red-500 dark:text-red-400">!</span>}
    </span>
  );
};

export default FileChip;
